import { defineStore } from "pinia";
import { ref } from "vue";
import { useAdminLeaveStore } from "@/stores/adminLeaveStore";
import { useProjectStore } from "@/stores/projectStore";
import { useCrmClientStore } from "@/stores/crmClientStore";
import { useTaskStore } from "@/stores/taskStore";
import { useExpenseStore } from "@/stores/expenseStore";
import { useInvoiceStore } from "@/stores/invoiceStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useAdminAttendanceStatsStore } from "@/stores/adminAttendanceStatsStore";
import { useAdminAttendanceStore } from "@/stores/adminAttendanceStore";
import { MODULE_KEYS } from "@/config/modules-registry";
import { ensurePlanAccessLoaded, loadIfModule, loadIfAnyModule } from "@/utils/planAccess";

function yRange(year) {
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

function fmtYMD(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function startOfWeekMonday(d = new Date()) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfWeekSunday(d = new Date()) {
  const s = startOfWeekMonday(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
}

function rangeForPeriod(period, year) {
  const now = new Date();
  const isCurrentYear = now.getFullYear() === year;
  const anchor = isCurrentYear ? now : new Date(year, 0, 1);

  if (period === "today") {
    const d = new Date(anchor);
    d.setHours(0, 0, 0, 0);
    return { date_from: fmtYMD(d), date_to: fmtYMD(d) };
  }

  if (period === "this_week") {
    return {
      date_from: fmtYMD(startOfWeekMonday(anchor)),
      date_to: fmtYMD(endOfWeekSunday(anchor)),
    };
  }

  if (period === "this_month") {
    const y = anchor.getFullYear();
    const m = anchor.getMonth();
    return {
      date_from: fmtYMD(new Date(y, m, 1)),
      date_to: fmtYMD(new Date(y, m + 1, 0)),
    };
  }

  return { date_from: `${year}-01-01`, date_to: `${year}-12-31` };
}

function netFromStats(invStats, expStats) {
  const revenue = Number(invStats?.total ?? invStats?.revenue ?? 0);
  const expenses = Number(expStats?.total ?? 0);
  return revenue - expenses;
}

export const useAdminDashboardStore = defineStore("adminDashboard", () => {
  const loading = ref(false);
  const loadedYear = ref(null);
  const earnings = ref(0);
  const profitSelectedYear = ref(0);
  const profitThisWeek = ref(0);
  const profitPrevWeek = ref(0);
  const earningsThisMonth = ref(0);
  const earningsPrevMonth = ref(0);

  let inflight = null;

  async function loadLeavesYear(year) {
    const adminLeaveStore = useAdminLeaveStore();
    const { from, to } = yRange(year);
    await Promise.all([
      adminLeaveStore.ensureStats({ date_from: from, date_to: to }),
      adminLeaveStore.ensureAll({
        page: 1,
        per_page: 1,
        date_from: from,
        date_to: to,
      }),
      adminLeaveStore.ensureChart?.({
        year,
        date_from: from,
        date_to: to,
      }),
    ]);
  }

  async function loadYearFinance(year) {
    const invoiceStore = useInvoiceStore();
    const expenseStore = useExpenseStore();
    const { from, to } = yRange(year);

    const [invYear, expYear] = await Promise.all([
      invoiceStore.ensureStatsForRange({ from, to }),
      expenseStore.ensureStatsForRange({ date_from: from, date_to: to }),
    ]);

    const net = netFromStats(invYear, expYear);
    earnings.value = net;
    profitSelectedYear.value = net;
  }

  async function loadProfitWeekCompare(year) {
    const invoiceStore = useInvoiceStore();
    const expenseStore = useExpenseStore();
    const isCurrentYear = year === new Date().getFullYear();

    if (!isCurrentYear) {
      profitThisWeek.value = 0;
      profitPrevWeek.value = 0;
      return;
    }

    const now = new Date();
    const wStart = startOfWeekMonday(now);
    const wEnd = endOfWeekSunday(now);
    const lastStart = new Date(wStart);
    lastStart.setDate(lastStart.getDate() - 7);
    const lastEnd = new Date(wEnd);
    lastEnd.setDate(lastEnd.getDate() - 7);

    const [invW, expW, invLW, expLW] = await Promise.all([
      invoiceStore.ensureStatsForRange({ from: fmtYMD(wStart), to: fmtYMD(wEnd) }),
      expenseStore.ensureStatsForRange({
        date_from: fmtYMD(wStart),
        date_to: fmtYMD(wEnd),
      }),
      invoiceStore.ensureStatsForRange({
        from: fmtYMD(lastStart),
        to: fmtYMD(lastEnd),
      }),
      expenseStore.ensureStatsForRange({
        date_from: fmtYMD(lastStart),
        date_to: fmtYMD(lastEnd),
      }),
    ]);

    profitThisWeek.value = netFromStats(invW, expW);
    profitPrevWeek.value = netFromStats(invLW, expLW);
  }

  async function loadEarningsMonthCompare(year) {
    const invoiceStore = useInvoiceStore();
    const expenseStore = useExpenseStore();
    const now = new Date();
    const isCurrentYear = year === now.getFullYear();
    const m = isCurrentYear ? now.getMonth() : 0;

    const m1Start = new Date(year, m, 1);
    const m1End = new Date(year, m + 1, 0);
    const prevStart = new Date(year, m - 1, 1);
    const prevEnd = new Date(year, m, 0);

    const [invThis, expThis, invPrev, expPrev] = await Promise.all([
      invoiceStore.ensureStatsForRange({
        from: fmtYMD(m1Start),
        to: fmtYMD(m1End),
      }),
      expenseStore.ensureStatsForRange({
        date_from: fmtYMD(m1Start),
        date_to: fmtYMD(m1End),
      }),
      invoiceStore.ensureStatsForRange({
        from: fmtYMD(prevStart),
        to: fmtYMD(prevEnd),
      }),
      expenseStore.ensureStatsForRange({
        date_from: fmtYMD(prevStart),
        date_to: fmtYMD(prevEnd),
      }),
    ]);

    earningsThisMonth.value = netFromStats(invThis, expThis);
    earningsPrevMonth.value = netFromStats(invPrev, expPrev);
  }

  async function loadDashboard(year, options = {}) {
    const force = options.force === true;
    const key = Number(year);

    if (!force && inflight) {
      return inflight;
    }

    if (!force && loadedYear.value === key && !loading.value) {
      return;
    }

    inflight = (async () => {
      loading.value = true;

      try {
        const projectStore = useProjectStore();
        const taskStore = useTaskStore();
        const clientStore = useCrmClientStore();
        const employeeStore = useEmployeeStore();
        const adminAttendanceStatsStore = useAdminAttendanceStatsStore();
        const adminAttendanceStore = useAdminAttendanceStore();

        await ensurePlanAccessLoaded();

        const todayRange = rangeForPeriod("today", key);
        const yearRange = yRange(key);

        const optionalModuleLoads = [
          loadIfModule(MODULE_KEYS.PROJECTS, () =>
            projectStore.ensureList({ page: 1, per_page: 5000 }),
          ),
          loadIfAnyModule([MODULE_KEYS.TASKS, MODULE_KEYS.TASK_BOARD], () =>
            taskStore.ensureList({ page: 1, per_page: 5000 }),
          ),
          loadIfModule(MODULE_KEYS.CRM_CLIENTS, () =>
            clientStore.ensureClients({ per_page: 5000 }),
          ),
          loadIfModule(MODULE_KEYS.HRM_EMPLOYEE, () =>
            employeeStore.ensureEmployees({ per_page: 5000, page: 1 }),
          ),
          loadIfModule(MODULE_KEYS.HRM_ATTENDANCE, () =>
            adminAttendanceStatsStore.ensureStats({
              date_from: yearRange.from,
              date_to: yearRange.to,
            }),
          ),
          loadIfModule(MODULE_KEYS.HRM_ATTENDANCE, () =>
            adminAttendanceStore.ensureStats({
              date_from: todayRange.date_from,
              date_to: todayRange.date_to,
            }),
          ),
          loadIfModule(MODULE_KEYS.HRM_ATTENDANCE, () =>
            adminAttendanceStore.ensureRecent({
              limit: 6,
              date_from: todayRange.date_from,
              date_to: todayRange.date_to,
            }),
          ),
          loadIfModule(MODULE_KEYS.HRM_ATTENDANCE, () => loadLeavesYear(key)),
          loadIfAnyModule(
            [MODULE_KEYS.FA_SALES, MODULE_KEYS.FA_ACCOUNTING],
            () => loadYearFinance(key),
          ),
          loadIfAnyModule(
            [MODULE_KEYS.FA_SALES, MODULE_KEYS.FA_ACCOUNTING],
            () => loadProfitWeekCompare(key),
          ),
          loadIfAnyModule(
            [MODULE_KEYS.FA_SALES, MODULE_KEYS.FA_ACCOUNTING],
            () => loadEarningsMonthCompare(key),
          ),
        ];

        await Promise.all(optionalModuleLoads);

        loadedYear.value = key;
      } finally {
        loading.value = false;
        inflight = null;
      }
    })();

    return inflight;
  }

  return {
    loading,
    loadedYear,
    earnings,
    profitSelectedYear,
    profitThisWeek,
    profitPrevWeek,
    earningsThisMonth,
    earningsPrevMonth,
    loadDashboard,
  };
});
